import React, { useState, useEffect } from 'react';
import { BallTriangle } from 'react-loader-spinner';
import toast from 'react-hot-toast';
import studentService from 'ApiService/Teacher/Student';
import Nodatafound from 'pages/Common/Nodatafound';
import InputField from 'components/fields/InputField';
import Card from 'components/card';
import ConfirmationModal from 'pages/Common/ConfirmationModal';

const AddMockMarks = ({ classData, setDisable, disable, handleBack }) => {
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [newSubjectName, setNewSubjectName] = useState('');
  const [isNewSubject, setIsNewSubject] = useState(false);
  const [marks, setMarks] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [totalMarks, setTotalMarks] = useState('100');
  const [mockTestDate, setMockTestDate] = useState('');
  const [existingMarksId, setExistingMarksId] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [isModalOpenConfirmation, setIsModalOpenConfirmation] = useState(false);

  const validateMarks = (value) => {
    if (!value) return true;
    const numValue = parseFloat(value);
    return numValue >= 0 && numValue <= parseFloat(totalMarks);
  };

  const getStudents = async () => {
    try {
      const response = await studentService.getStudent(
        classData.courseId,
        classData.batchId
      );
      if (response.data.success) {
        const studentData = response.data.data.map((student) => ({
          id: student._id,
          name: student.name,
          instituteId: student.instituteId,
        }));
        setStudents(studentData);
        const initialMarks = {};
        studentData.forEach((student) => {
          initialMarks[student.id] = '';
        });
        setMarks(initialMarks);
      }
    } catch (error) {
      toast.error('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await studentService.getUniqueSubjects(
        classData.courseId,
        classData.batchId
      );
      if (response.data.success) {
        setSubjects(response.data.subjects);
      }
    } catch (error) {
      toast.error('Failed to fetch subjects');
    }
  };

  const fetchExistingMarks = async () => {
    if (!mockTestDate || (!selectedSubject && !newSubjectName)) return;

    try {
      const response = await studentService.getMockMarksByDate(
        classData.courseId,
        classData.batchId,
        mockTestDate,
        isNewSubject ? newSubjectName : selectedSubject
      );

      if (response.data.success && response.data.data.length > 0) {
        const existingMarks = {};
        const existingIds = {};

        response.data.data.forEach((mark) => {
          existingMarks[mark.studentId] = mark.Marks.toString();
          existingIds[mark.studentId] = mark._id;
        });

        setMarks((prev) => ({
          ...prev,
          ...existingMarks,
        }));
        setExistingMarksId(existingIds);
        setTotalMarks(response.data.data[0].TotalMarks.toString());
        setIsEditMode(true);
        toast.success('Existing marks loaded');
      } else {
        const initialMarks = {};
        students.forEach((student) => {
          initialMarks[student.id] = '';
        });
        setMarks(initialMarks);
        setIsEditMode(false);
        setExistingMarksId({});
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch marks');

      const initialMarks = {};
      students.forEach((student) => {
        initialMarks[student.id] = '';
      });
      setMarks(initialMarks);
    }
  };

  useEffect(() => {
    if (classData.courseId && classData.batchId) {
      getStudents();
      fetchSubjects();
    }
  }, [classData]);

  useEffect(() => {
    fetchExistingMarks();
  }, [mockTestDate, selectedSubject]);

  const handleMarkChange = (studentId, value) => {
    const numValue = parseFloat(value);
    const maxMarks = parseFloat(totalMarks);
    if (value === '' || (numValue >= 0 && numValue <= maxMarks)) {
      setMarks((prev) => ({
        ...prev,
        [studentId]: value,
      }));

      const updatedMarks = { ...marks, [studentId]: value };
      const allMarksEntered = Object.values(updatedMarks).every(
        (mark) => mark !== ''
      );
      const hasValidSubject = isNewSubject
        ? newSubjectName.trim()
        : selectedSubject;
      setDisable(!hasValidSubject || !mockTestDate);
    }
  };

  const handleTotalMarksChange = (value) => {
    if (value === '' || parseFloat(value) >= 0) {
      setTotalMarks(value);
      // Validate existing marks against new total
      const newMarks = { ...marks };
      Object.entries(marks).forEach(([studentId, mark]) => {
        if (mark !== '' && parseFloat(mark) > parseFloat(value)) {
          newMarks[studentId] = '';
        }
      });
      setMarks(newMarks);
    }
  };

  const handleSubjectChange = (e) => {
    const value = e.target.value;
    if (value === 'new') {
      setIsNewSubject(true);
      setSelectedSubject('');
    } else {
      setIsNewSubject(false);
      setSelectedSubject(value);
      setNewSubjectName('');
    }
  };
  const handleConfirmSubmit = () => {
    if (!mockTestDate.trim()) {
      toast.error('Please select a mock test date');
      return;
    }

    if (!totalMarks || parseFloat(totalMarks) <= 0) {
      toast.error('Please enter valid total marks');
      return;
    }

    if (
      (!selectedSubject && !newSubjectName.trim()) ||
      (isNewSubject && !newSubjectName.trim())
    ) {
      toast.error('Please select or enter a subject name');
      return;
    }

    const allMarksValid = Object.values(marks).every((mark) =>
      validateMarks(mark)
    );
    if (!allMarksValid) {
      toast.error(`All marks should be between 0 and ${totalMarks}`);
      return;
    }

    setIsModalOpenConfirmation(true);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    try {
      setSubmitting(true);
      const marksData = Object.entries(marks).map(([studentId, mark]) => ({
        studentId,
        marks: mark === '' ? 0 : parseFloat(mark),
        _id: existingMarksId[studentId],
      }));

      const payload = {
        courseId: classData.courseId,
        batchId: classData.batchId,
        subjectName: isNewSubject ? newSubjectName : selectedSubject,
        totalMarks: parseFloat(totalMarks),
        testDate: mockTestDate,
        marksData,
      };

      const response = await studentService.submitMarks(payload);

      if (response.data.success) {
        toast.success(
          isEditMode
            ? 'Marks updated successfully'
            : 'Marks submitted successfully'
        );
        setIsModalOpenConfirmation(false);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <BallTriangle color="#5431ed" />
      </div>
    );
  }

  return (
    <Card extra={'w-full sm:overflow-auto p-4'}>
      <div className="mb-4 flex flex-col space-y-4">
        {/* Subject Selection/Creation */}
        <div className="flex items-center space-x-4">
          <select
            value={isNewSubject ? 'new' : selectedSubject}
            onChange={handleSubjectChange}
            className="w-full rounded border px-3 py-2 dark:bg-navy-800"
          >
            <option value="">Select Subject</option>
            {subjects.map((subject) => (
              <option key={subject} value={subject}>
                {subject}
              </option>
            ))}
            <option value="new">+ Add New Subject</option>
          </select>
        </div>

        {/* Test Date */}
        <div className="flex items-center space-x-4">
          <InputField
            label="Test Date"
            type="date"
            value={mockTestDate}
            onChange={(e) => setMockTestDate(e.target.value)}
            className="w-full rounded border px-3 py-2 dark:bg-navy-800"
          />
        </div>

        {/* New Subject Input and Total Marks */}
        <div className="flex items-center space-x-4">
          {isNewSubject && (
            <input
              type="text"
              value={newSubjectName}
              onChange={(e) => setNewSubjectName(e.target.value)}
              className="w-full rounded border px-3 py-2 dark:bg-navy-800"
              placeholder="Enter new subject name"
            />
          )}
          <div className="flex items-center space-x-2">
            <label className="whitespace-nowrap">Total Marks:</label>
            <input
              type="number"
              min="0"
              value={totalMarks}
              onChange={(e) => handleTotalMarksChange(e.target.value)}
              className="w-24 rounded border px-3 py-2 dark:bg-navy-800"
              placeholder="Total marks"
            />
          </div>
        </div>

        {/* No Students Message */}
        {!students.length && !loading && (
          <div className="text-center text-gray-600 dark:text-gray-400">
            <Nodatafound title="No students found in this class." />
          </div>
        )}

        {/* Marks Table */}
        {students.length > 0 && (
          <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-white/10">
                  <th className="px-4 py-3 text-center text-xs font-bold">
                    SR.NO
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-bold">
                    NAME
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-bold">
                    MARKS
                  </th>
                </tr>
              </thead>
              <tbody>
                {students.map((student, index) => (
                  <tr
                    key={student.id}
                    className="border-b border-gray-200 dark:border-white/10"
                  >
                    <td className="px-4 py-3 text-center">{index + 1}</td>
                    <td className="px-4 py-3 text-center">{student.name}</td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="number"
                        min="0"
                        max={totalMarks}
                        value={marks[student.id]}
                        onChange={(e) =>
                          handleMarkChange(student.id, e.target.value)
                        }
                        className="w-20 rounded border px-2 py-1 text-center dark:bg-navy-800"
                        placeholder={`0-${totalMarks}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex flex-col justify-between px-3 pt-2 md:flex-row md:px-8">
          <button
            onClick={handleBack}
            className="mb-3 cursor-pointer rounded-xl bg-lightPrimary px-16 py-2 font-medium text-navy-700 transition duration-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10 dark:active:bg-white/20 md:mb-0"
          >
            Prev
          </button>
          <div className="ml-auto">
            {students.length > 0 && (
              <button
                disabled={submitting || disable}
                onClick={handleConfirmSubmit}
                className="linear cursor-pointer rounded-xl bg-brand-900 px-16 py-2 font-medium text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90"
              >
                {submitting
                  ? 'Submitting...'
                  : isEditMode
                  ? 'Update Marks'
                  : 'Submit Marks'}
              </button>
            )}
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={isModalOpenConfirmation}
        onClose={() => setIsModalOpenConfirmation(false)}
        onConfirm={handleSubmit}
        title={isEditMode ? 'Confirm Marks Update' : 'Confirm Marks Submission'}
        message={
          isEditMode
            ? 'Are you sure you want to update these marks?'
            : 'Are you sure you want to submit these marks?'
        }
        confirmText={submitting ? 'Processing...' : 'Yes, Proceed'}
        cancelText="Cancel"
        icon="warning"
        disableConfirm={submitting}
      />
    </Card>
  );
};

export default AddMockMarks;
